import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  Megaphone,
  Plus,
  Pin,
  Link as LinkIcon,
  Send,
  Shield,
  Filter,
  Loader2,
  AlertTriangle,
  Trash2,
  Trophy,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

type Audience = "team" | "staff";

type TeamPost = {
  id: string;
  created_at: string;
  author_id: string;
  author_name: string;
  title: string;
  content: string;
  url: string | null;
  pinned: boolean;
  audience: Audience;
};

type UnifiedEvent = {
  id: string;
  type: "match" | "training";
  date: string;
  opponent?: string | null;
  location?: string | null;
  score?: string | null;
  victory?: boolean | null;
  notes?: string | null;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const POSTER_TEAM_ROLES = ["owner", "coach", "assistant"];
const POSTER_CLUB_ROLES = ["dirigeant", "cadre_technique"];
const STAFF_VIEWER_TEAM_ROLES = ["owner", "coach", "assistant"];
const STAFF_VIEWER_CLUB_ROLES = ["dirigeant", "cadre_technique"];

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function TeamFeed() {
  const { clubId, teamId } = useParams<{ clubId: string; teamId: string }>();

  // ── Data state ──
  const [teamName, setTeamName] = useState("");
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUserName, setCurrentUserName] = useState("");
  const [teamRole, setTeamRole] = useState<string | null>(null);
  const [clubRole, setClubRole] = useState<string | null>(null);
  const [playersCanPost, setPlayersCanPost] = useState(false);
  const [posts, setPosts] = useState<TeamPost[]>([]);
  const [nextEvent, setNextEvent] = useState<UnifiedEvent | null>(null);
  const [lastEvent, setLastEvent] = useState<UnifiedEvent | null>(null);

  // ── UI state ──
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<"all" | "team" | "staff">("all");
  const [openCreate, setOpenCreate] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  // ── Form state ──
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [url, setUrl] = useState("");
  const [pinned, setPinned] = useState(false);
  const [audience, setAudience] = useState<Audience>("team");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // ── Derived permissions ──
  const isStaff =
    (!!teamRole && POSTER_TEAM_ROLES.includes(teamRole)) ||
    (!!clubRole && POSTER_CLUB_ROLES.includes(clubRole));

  const canPost = isStaff || (teamRole === "player" && playersCanPost);

  const canSeeStaff =
    (!!teamRole && STAFF_VIEWER_TEAM_ROLES.includes(teamRole)) ||
    (!!clubRole && STAFF_VIEWER_CLUB_ROLES.includes(clubRole));

  // ── Load ──
  useEffect(() => {
    if (!teamId || !clubId) return;
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const { data: { user }, error: authErr } = await supabase.auth.getUser();
        if (authErr) throw authErr;
        if (!user) throw new Error("Utilisateur non connecté.");

        const [
          teamRes,
          profileRes,
          teamRoleRes,
          clubRoleRes,
          settingsRes,
          postsRes,
          matchesRes,
          trainingsRes,
        ] = await Promise.all([
          supabase.from("teams").select("team_name").eq("id", teamId).maybeSingle(),
          supabase.from("user_profiles")
            .select("full_name, pseudo, public_use_pseudo")
            .eq("id", user.id)
            .maybeSingle(),
          supabase.from("team_members")
            .select("role")
            .eq("team_id", teamId)
            .eq("user_id", user.id)
            .maybeSingle(),
          supabase.from("club_members")
            .select("role")
            .eq("club_id", clubId)
            .eq("user_id", user.id)
            .maybeSingle(),
          supabase.from("team_settings")
            .select("players_can_post")
            .eq("team_id", teamId)
            .maybeSingle(),
          supabase.from("team_posts")
            .select("id, created_at, author_id, author_name, title, content, url, pinned, audience")
            .eq("team_id", teamId)
            .order("pinned", { ascending: false })
            .order("created_at", { ascending: false })
            .limit(50),
          supabase.from("matches")
            .select("id, date, opponent, location, score, victory")
            .eq("team_id", teamId)
            .order("date", { ascending: false })
            .limit(20),
          supabase.from("trainings")
            .select("id, date, notes")
            .eq("team_id", teamId)
            .order("date", { ascending: false })
            .limit(20),
        ]);

        if (!cancelled) {
          setCurrentUserId(user.id);

          const p = profileRes.data;
          const displayName =
            p?.public_use_pseudo && p?.pseudo
              ? p.pseudo
              : p?.full_name?.trim() || user.email?.split("@")[0] || "Membre";
          setCurrentUserName(displayName);

          setTeamName(teamRes.data?.team_name?.trim() || "Équipe");
          setTeamRole(teamRoleRes.data?.role ?? null);
          setClubRole(clubRoleRes.data?.role ?? null);
          setPlayersCanPost(settingsRes.data?.players_can_post ?? false);

          setPosts((postsRes.data || []) as TeamPost[]);

          // Build unified event list
          const now = Date.now();
          const events: UnifiedEvent[] = [
            ...(matchesRes.data || []).map((m: any) => ({
              id: m.id,
              type: "match" as const,
              date: m.date,
              opponent: m.opponent,
              location: m.location,
              score: m.score,
              victory: m.victory,
            })),
            ...(trainingsRes.data || []).map((t: any) => ({
              id: t.id,
              type: "training" as const,
              date: t.date,
              notes: t.notes,
            })),
          ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

          const upcoming = events.filter((e) => new Date(e.date).getTime() >= now);
          const past = events.filter((e) => new Date(e.date).getTime() < now);

          setNextEvent(upcoming[0] ?? null);
          setLastEvent(past[past.length - 1] ?? null);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || "Impossible de charger le fil d'actualité.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    // Realtime subscription for new posts
    const channel = supabase
      .channel(`team-feed-${teamId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "team_posts", filter: `team_id=eq.${teamId}` },
        (payload) => {
          if (!cancelled) {
            setPosts((prev) => {
              if (prev.some((p) => p.id === payload.new.id)) return prev;
              const newPost = payload.new as TeamPost;
              const withNew = [newPost, ...prev];
              return withNew.sort((a, b) => {
                if (a.pinned !== b.pinned) return b.pinned ? 1 : -1;
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
              });
            });
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "team_posts", filter: `team_id=eq.${teamId}` },
        (payload) => {
          if (!cancelled) {
            setPosts((prev) => prev.filter((p) => p.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [teamId, clubId]);

  // ── Submit post ──
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canPost || !teamId || !currentUserId) return;

    setSubmitting(true);
    setSubmitError("");

    const { data: inserted, error: insertErr } = await supabase
      .from("team_posts")
      .insert({
        team_id: teamId,
        author_id: currentUserId,
        author_name: currentUserName,
        title: title.trim() || "Sans titre",
        content: content.trim(),
        url: url.trim() || null,
        pinned,
        audience,
      })
      .select("id, created_at, author_id, author_name, title, content, url, pinned, audience")
      .single();

    setSubmitting(false);

    if (insertErr) {
      setSubmitError(insertErr.message);
      return;
    }

    setPosts((prev) => {
      if (prev.some((p) => p.id === inserted.id)) return prev;
      const withNew = [inserted as TeamPost, ...prev];
      return withNew.sort((a, b) => {
        if (a.pinned !== b.pinned) return b.pinned ? 1 : -1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
    });
    setOpenCreate(false);
    resetForm();
  }

  // ── Delete post ──
  async function handleDelete(postId: string) {
    if (!window.confirm("Supprimer cette publication ?")) return;
    setDeleting(postId);
    const { error: delErr } = await supabase.from("team_posts").delete().eq("id", postId);
    setDeleting(null);
    if (delErr) { alert(delErr.message); return; }
    setPosts((prev) => prev.filter((p) => p.id !== postId));
  }

  function resetForm() {
    setTitle("");
    setContent("");
    setUrl("");
    setPinned(false);
    setAudience("team");
    setSubmitError("");
  }

  // ── Filtered posts ──
  const filteredPosts = useMemo(() => {
    return posts.filter((p) => {
      if (!canSeeStaff && p.audience === "staff") return false;
      if (filter === "all") return true;
      return p.audience === filter;
    });
  }, [posts, filter, canSeeStaff]);

  // ── Render ──
  if (loading) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6">
        <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 p-4 text-sm text-red-700 dark:text-red-300">
        <div className="inline-flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col items-center text-center gap-3 sm:flex-row sm:items-center sm:justify-between sm:text-left">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Actu équipe</div>
          <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Fil d'actualité{teamName ? ` • ${teamName}` : ""}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Infos, convocations, liens et notes pour l'équipe.
          </div>
        </div>

        {canPost && (
          <button
            type="button"
            onClick={() => setOpenCreate(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-sm"
          >
            <Plus className="h-4 w-4" />
            Publier
          </button>
        )}
      </div>

      {/* Next / Last event cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <EventCard title="Prochain événement" event={nextEvent} />
        <EventCard title="Dernier événement" event={lastEvent} />
      </div>

      {/* Filter bar */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <Filter className="h-4 w-4 text-gray-500" />
            Filtrer
          </div>
          <div className="flex flex-wrap gap-2">
            {(["all", "team", ...(canSeeStaff ? ["staff"] : [])] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f as any)}
                className={cx(
                  "px-3 py-2 rounded-xl text-sm border",
                  filter === f
                    ? "bg-gray-100 text-gray-900 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                    : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                )}
              >
                {f === "all" ? "Tout" : f === "team" ? "Équipe" : "Staff"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Create post modal */}
      {openCreate && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setOpenCreate(false); resetForm(); }} />
          <div className="absolute inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center p-3">
            <div className="w-full sm:max-w-2xl rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="font-semibold text-gray-900 dark:text-gray-100">Nouvelle publication</div>
                <button type="button" onClick={() => { setOpenCreate(false); resetForm(); }}
                  className="text-sm text-gray-600 dark:text-gray-300 hover:underline">
                  Fermer
                </button>
              </div>

              {submitError && (
                <div className="mb-3 rounded-xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                  {submitError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Titre *</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} required
                    placeholder="Ex : Convocation / Info / Rappel…"
                    className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700" />
                </div>

                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Contenu *</label>
                  <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={5} required
                    placeholder="Ton message…"
                    className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Audience</label>
                    <div className="mt-1 grid grid-cols-2 gap-2">
                      {(["team", "staff"] as Audience[]).map((a) => (
                        <button key={a} type="button" onClick={() => setAudience(a)}
                          className={cx(
                            "px-3 py-2 rounded-xl text-sm border inline-flex items-center justify-center gap-1.5",
                            audience === a
                              ? "bg-gray-100 text-gray-900 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                              : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                          )}>
                          {a === "staff" && <Shield className="h-3.5 w-3.5" />}
                          {a === "team" ? "Équipe" : "Staff"}
                        </button>
                      ))}
                    </div>
                    <label className="mt-3 inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
                      <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />
                      Épingler en haut
                    </label>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Lien (optionnel)</label>
                    <div className="mt-1 relative">
                      <LinkIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…"
                        className="w-full pl-9 pr-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700" />
                    </div>
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Vidéo, doc, planning…
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button type="button" onClick={() => { setOpenCreate(false); resetForm(); }}
                    className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800">
                    Annuler
                  </button>
                  <button type="submit" disabled={submitting}
                    className={cx(
                      "inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm",
                      "bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
                      submitting && "opacity-60 cursor-not-allowed"
                    )}>
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {submitting ? "Publication…" : "Publier"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Post list */}
      <div className="space-y-3">
        {filteredPosts.length === 0 ? (
          <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-8 flex flex-col items-center gap-2 text-gray-400 dark:text-gray-500">
            <Megaphone className="h-7 w-7" />
            <div className="text-sm">
              {posts.length === 0
                ? "Aucune publication pour l'instant."
                : "Aucune publication pour ce filtre."}
            </div>
          </div>
        ) : (
          filteredPosts.map((p) => {
            const isOwn = p.author_id === currentUserId;
            const isDeletable = isOwn || isStaff;
            return (
              <div key={p.id} className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">{p.title}</div>
                      {p.pinned && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-900 dark:bg-yellow-900/20 dark:text-yellow-200">
                          <Pin className="h-3.5 w-3.5" />Épinglé
                        </span>
                      )}
                      {p.audience === "staff" && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-900 dark:bg-purple-900/20 dark:text-purple-200">
                          <Shield className="h-3.5 w-3.5" />Staff
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className="inline-flex items-center gap-1">
                        <Megaphone className="h-3.5 w-3.5" />{p.author_name}
                      </span>
                      <span>•</span>
                      <span>{formatDate(p.created_at)}</span>
                    </div>
                  </div>

                  {isDeletable && (
                    <button type="button" onClick={() => handleDelete(p.id)}
                      disabled={deleting === p.id}
                      className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition disabled:opacity-40"
                      aria-label="Supprimer">
                      {deleting === p.id
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <Trash2 className="h-4 w-4" />}
                    </button>
                  )}
                </div>

                <div className="mt-3 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                  {p.content}
                </div>

                {p.url && (
                  <div className="mt-3">
                    <a href={p.url} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100 hover:underline">
                      <LinkIcon className="h-4 w-4" />Ouvrir le lien
                    </a>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Event card sub-component ─────────────────────────────────────────────────

function EventCard({ title, event }: { title: string; event: UnifiedEvent | null }) {
  function cx(...classes: Array<string | false | undefined | null>) {
    return classes.filter(Boolean).join(" ");
  }

  return (
    <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">{title}</div>
      {event ? (
        <div>
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <span className={cx(
              "px-2 py-0.5 rounded-full",
              event.type === "match"
                ? "bg-blue-100 text-blue-900 dark:bg-blue-900/20 dark:text-blue-200"
                : "bg-green-100 text-green-900 dark:bg-green-900/20 dark:text-green-200"
            )}>
              {event.type === "match" ? "Match" : "Entraînement"}
            </span>
            <span className="text-gray-500 dark:text-gray-400">
              {new Date(event.date).toLocaleString("fr-FR", {
                weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
              })}
            </span>
          </div>

          <div className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            {event.type === "match"
              ? `vs ${event.opponent || "Adversaire inconnu"}`
              : "Entraînement"}
          </div>

          {event.type === "match" && event.score && (
            <div className="mt-1 flex items-center gap-2 text-sm">
              <span className="font-mono font-medium text-gray-900 dark:text-gray-100">{event.score}</span>
              {event.victory !== null && (
                <span className={cx(
                  "inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full",
                  event.victory
                    ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                    : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                )}>
                  <Trophy className="h-3 w-3" />
                  {event.victory ? "Victoire" : "Défaite"}
                </span>
              )}
            </div>
          )}

          {event.type === "training" && event.notes && (
            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400 truncate">{event.notes}</div>
          )}

          {event.location && (
            <div className="mt-1 text-sm text-gray-500 dark:text-gray:400">{event.location}</div>
          )}

          <div className="mt-3 text-sm">
            <a href="calendar" className="text-gray-900 dark:text-gray-100 hover:underline">
              Voir le calendrier →
            </a>
          </div>
        </div>
      ) : (
        <div className="text-sm text-gray-500 dark:text-gray-400">Aucun événement.</div>
      )}
    </div>
  );
}
